import {Component, computed, OnDestroy, Signal, signal} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {ApiService} from "../../services/api.service";
import {BootstrapIcon, HeaderService} from "../../services/header.service";
import {MalfunctionStatus, Report} from "../../shared/interfaces/report";
import {ReportService} from "../error-report/data-access/report.service";
import {CommonModule} from "@angular/common";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {KdSolutionComponent} from "../knowledge-database/kd-solution/kd-solution.component";
import {KnowledgeBaseService, NodeType} from "../knowledge-database/data-access/knowledge-base.service";
import {SolutionService} from "../knowledge-database/data-access/solution.service";
import {catchError, EMPTY, filter, forkJoin, map, Observable, of, Subject, switchMap, tap,} from "rxjs";
import {UserService} from "../../services/user.service";
import {Escalation, EscalationStatus, EscalationUpdate} from "../escalation/escalation.model";
import {Solution} from "../../shared/interfaces/solution";
import {MatDialog} from "@angular/material/dialog";
import {ConfirmationDialogComponent} from "../../shared/confirmation-dialog/confirmation-dialog.component";
import {NotificationService} from "../../shared/data-access/notification.service";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";

type Status = 'loading' | 'loaded' | 'error';

interface ReportState {
  report: Report,
  escalation?: Escalation,
  status: Status
}

export interface ViewModel {
  report: Report,
  escalation?: Escalation,
  created_by_user: string | null,
  solved_by_user: string | null,
  escalationAcceptedUserName: string | null,
}

@Component({
  selector: "app-report",
  templateUrl: "./report.component.html",
  styleUrls: ["./report.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    KdSolutionComponent,
  ],
})
export class ReportComponent implements OnDestroy {
  private updateEscalation$ = new Subject<Partial<Escalation>>()
  protected vm$: Observable<Omit<ReportState, 'status'>> = this.route.paramMap
    .pipe(
      map((params) => params.get("id")),
      filter((id) => !!id),
      switchMap((id) => this.apiService.getReportById(id!)),
      switchMap((report: Report) => {

        // get latest active escalation
        const activeEscalation$ = report.status === MalfunctionStatus.ESCALATED
          ? this.reportService.getActiveEscalation(report.id)
          : of(undefined)

        return forkJoin({
          report: of(report),
          escalation: activeEscalation$ ? activeEscalation$ : of(undefined)
        });
      }),
      map(({report, escalation}) => ({
        report,
        escalation,
      })),
      tap((viewModel) => {
        this.updateKnowledgeBaseItems(viewModel.report);
        this.solutionService.selectedSolutionId$.next(
          (viewModel.report.solution as Solution).id
        );
        this.header.setHeaderSubTitle(
          `${
            this.knowledgeBaseService.expandedNodes().productionLine?.name
          } - ${
            this.knowledgeBaseService.expandedNodes().workplace?.name
          } - ${this.knowledgeBaseService.expandedNodes().malfunction?.name}`
        );
      }),
      catchError((error) => {
        console.error("Error fetching data:", error);
        return EMPTY;
      }),
    )

  private state = signal<ReportState>({
    report: {} as Report,
    escalation: undefined,
    status: 'loading'
  })


  vm: Signal<ViewModel> = computed(() => {
    return {
      report: this.state().report,
      escalation: this.state().escalation,
      created_by_user: this.createdByUserName(),
      solved_by_user: this.solvedByUserName(),
      escalationAcceptedUserName: this.escalationAcceptedUserName(),
    }
  })

  private createUserNameSignal(userIdComputed: () => string | null | undefined) {
    return toSignal(
      toObservable(computed(userIdComputed)).pipe(
        switchMap(userId => {
          if (!userId) {
            return of(null);
          }
          return this.apiService.getUserName(userId).pipe(
            map(user => `${user.first_name} ${user.last_name}`),
            catchError(() => of('Benutzer konnte nicht gefunden werden'))
          );
        })
      ),
      {initialValue: null}
    );
  }

  createdByUserName = this.createUserNameSignal(() => {
    const report = this.state().report;
    return report?.created_by;
  });

  solvedByUserName = this.createUserNameSignal(() => {
    const report = this.state().report;
    return report?.solved_by;
  });

  escalationAcceptedUserName = this.createUserNameSignal(() => {
    const escalation = this.state().escalation;
    return escalation?.assigned_to_user;
  });


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private header: HeaderService,
    private userService: UserService,
    private apiService: ApiService,
    private reportService: ReportService,
    protected knowledgeBaseService: KnowledgeBaseService,
    protected solutionService: SolutionService,
    private dialog: MatDialog,
    private notification: NotificationService
  ) {
    this.header.setHeaderTitle("Stillstandsmeldung");
    this.header.setHeaderIcon(BootstrapIcon.standstill);
    this.knowledgeBaseService.activeModule.set("downtimeDetails");

    this.vm$.subscribe((vm) =>
      this.state.update((state) => ({
        ...state,
        report: vm.report,
        escalation: vm.escalation,
        status: 'loaded'
      }))
    )

    this.updateEscalation$.subscribe((updatedValues) => {
        console.log(updatedValues);
        this.state.update((state) => ({
            ...state,
            escalation: state.escalation ? {
                ...state.escalation,
                ...updatedValues
              }
              : undefined
          })
        )
      }
    )
  }

  ngOnDestroy() {
    this.reportService.currentReport$.next(null);
    this.reportService.resetState();
  }

  updateKnowledgeBaseItems(data: any) {
    const items = [
      {
        type: "productionLine",
        id: data.workplace.production_line.id,
        name: data.workplace.production_line.name,
      },
      {
        type: "workplace",
        id: data.workplace.id,
        name: data.workplace.name,
      },
      {
        type: "category",
        id: data.category.id,
        name: data.category.name,
      },
      {
        type: "malfunction",
        id: data.malfunction.id,
        name: data.malfunction.name,
      },
      {
        type: "solution",
        id: data.solution.id,
        name: data.solution.name,
      },
    ];

    items.forEach((item) => {
      this.knowledgeBaseService.updateSelectedItems(item.type as NodeType, {
        id: item.id,
        name: item.name,
        type: item.type as NodeType,
      });
    });
  }

  solveReport() {
    this.reportService.solveEscalatedReport({
      report: this.vm().report,
      solutionId: this.solutionService.solution()?.id,
    })
  }

  handleEscalationEvent(event: EscalationUpdate) {
    this.updateEscalation$.next({
      assigned_to_user: this.userService.user()!.id,
      status: event.action === 'accept' || event.action === 'takeover' ? EscalationStatus.ACCEPTED : EscalationStatus.IN_PROGRESS
    })
  }

  deleteReport(reportId: string) {
    console.log("Delete report with ID:", reportId);
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: "Möchten Sie diese Stillstandsmeldung wirklich löschen?",
        name: "Stillstandsmeldung löschen",
        actions: ['cancel', 'confirm']
      }
    })
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.apiService.deleteReport(reportId)
          .pipe(
            catchError(error => {
              this.notification.showNotification('Fehler beim Löschen der Stillstandsmeldung', 3000);
              console.error('Error deleting report:', error);
              return EMPTY;
            })
          )
          .subscribe(result => {
            if (result) {
              this.notification.showNotification('Stillstandsmeldung erfolgreich gelöscht', 3000);
              this.router.navigate(['/dashboard']);
            }
          })
      }
    })
  }

  selectReportSolution(solution: Solution) {
    this.reportService.update$.next({properties: {solution: solution.id}});
    location.reload();
  }
}
